/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable react/jsx-sort-props */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Box, Button, CircularProgress, Select, Option, Typography, Tabs, TabList, Tab, TabPanel } from '@mui/joy';
import { observer } from 'mobx-react-lite';

import BatteryBarLevel0 from '../assets/images/BatteryBarLevel0.svg?react';
import BatteryBarLevel2 from '../assets/images/BatteryBarLevel2.svg?react';
import BatteryBarLevel4 from '../assets/images/BatteryBarLevel4.svg?react';
import BatteryBarLevel6 from '../assets/images/BatteryBarLevel6.svg?react';
import Drone from '../assets/images/Drone.svg?react';
import DynamicTable, { TableColumn } from '../components/DynamicTable';
import ResponsiveLayout from '../components/layout/ResponsiveLayout';
import TableHeader from '../components/TableHeader';
import { DroneStatusTag } from '../general-utilities/droneUtils';
import { Device, DeviceHeartBeat, DeviceType } from '../models/Device';
import { DeviceService } from '../services/DeviceService';
import organizationStore from '../stores/OrganizationStore';
import { DeviceCategories } from '../types/DeviceCategories';

type DeviceInfo = {
  id: string;
  name: string;
  serialNumber: string;
  device_type: DeviceType | null;
  lastHeartbeat?: Date | null;
  batteryPercentage?: number;
  software: string;
};

const Devices = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedOrg } = organizationStore;

  const [devices, setDevices] = useState<DeviceInfo[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<Record<string, DeviceType>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [viewType, setViewType] = useState<'card' | 'list'>('list');
  const [selectedDeviceCategory, setSelectedDeviceCategory] = useState<DeviceCategories>(DeviceCategories.Drone);

  useEffect(() => {
    const fetchDevices = async (): Promise<void> => {
      if (!selectedOrg) {
        setError(t('devices.error_no_org'));
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        // Use the new getAllDevices method which handles organization filtering
        const devicesResult = await DeviceService.getAllDevices();

        if (!devicesResult.success) {
          throw new Error(devicesResult.error?.message || 'Failed to fetch devices');
        }

        const devicesList = devicesResult.data;
        const deviceInfoList: DeviceInfo[] = devicesList.map((device: Device) => {
          return {
            id: device.id,
            name: device.name,
            serialNumber: device.serial_number,
            device_type: device.device_type,
            lastHeartbeat: device.heart_beat?.timestamp ? new Date(device.heart_beat?.timestamp) : null,
            batteryPercentage: device.heart_beat?.battery_percentage,
            software: '-' // TODO
          };
        });

        setDevices(deviceInfoList);
      } catch (err) {
        setError(t('drones.error_fetch'));
      } finally {
        setLoading(false);
      }
    };

    const fetchDeviceTypes = async (): Promise<void> => {
      try {
        const res = await DeviceService.getDeviceTypes();

        if (!res.success) {
          throw new Error(res.error?.message || 'Failed to fetch device types');
        }

        setDeviceTypes(
          res.data.reduce(
            (acc: Record<string, DeviceType>, deviceType: DeviceType) => ({ ...acc, [deviceType.id]: deviceType }),
            {}
          )
        );
      } catch (err) {
        setError(t('drones.error_fetch'));
      } finally {
        setLoading(false);
      }
    };

    fetchDevices();
    fetchDeviceTypes();
  }, [selectedOrg, t]);

  // NOTE: 'device_type' and 'lastHeartbeat' are not real keys of DeviceInfo. We use render() function for these columns.
  const columns: TableColumn<DeviceInfo, keyof DeviceInfo>[] = [
    {
      header: '',
      key: 'name',
      render: () => (
        <div style={{}}>
          <Drone />
        </div>
      ),
      width: '4%'
    },
    {
      header: t('devices.name'),
      key: 'name',
      width: '16%'
    },
    {
      header: t('devices.serial_number'),
      key: 'serialNumber',
      width: '16%'
    },
    {
      header: t('devices.device_type'),
      key: 'device_type',
      render: (value: string | number | Date | DeviceType | undefined | null, row: DeviceInfo) => {
        return row.device_type?.name || t('devices.unknown');
      },
      width: '12%'
    },
    {
      header: t('devices.status.header'),
      key: 'name',
      render: (value: string | number | DeviceType | Date | null | undefined, row: DeviceInfo) => {
        const last = row.lastHeartbeat;
        return <DroneStatusTag last={last} />;
      },
      width: '8%'
    },
    {
      header: t('devices.last_heartbeat.header'),
      key: 'lastHeartbeat',
      render: (value: string | number | DeviceType | Date | null | undefined, row: DeviceInfo) => {
        const last = value as Date;
        return (
          <Box component='span' className='inline-block'>
            {last?.toLocaleString() || t('devices.last_heartbeat.none')}
          </Box>
        );
      },
      width: '16%'
    },
    {
      header: t('devices.battery'),
      key: 'batteryPercentage',
      render: (value: string | number | DeviceType | Date | null | undefined, row: DeviceInfo) =>
        value ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <Box component='span'>{`${value}%`}</Box>
              {
                [
                  <BatteryBarLevel0 style={{ marginLeft: '6px' }} />,
                  <BatteryBarLevel2 style={{ marginLeft: '6px' }} />,
                  <BatteryBarLevel4 style={{ marginLeft: '6px' }} />,
                  <BatteryBarLevel6 style={{ marginLeft: '6px' }} />
                ][[25, 50, 75, 100].findIndex((val) => val >= (value as number))]
              }
            </div>
          </>
        ) : (
          '-'
        ),
      width: '8%'
    },
    {
      header: t('devices.software'),
      key: 'software',
      render: (value: string | number | DeviceType | Date | null | undefined, row: DeviceInfo) =>
        (value as string) || '-',
      width: '8%'
    }
  ];

  const body = (() => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Typography color='danger' level='body-lg' sx={{ textAlign: 'center', mt: 2 }}>
          {error}
        </Typography>
      );
    }
    // Add an Actions column to the columns array
    const columnsWithActions: TableColumn<DeviceInfo, keyof DeviceInfo>[] = [
      ...columns,
      {
        header: t('general_button_labels.actions'),
        key: '' as keyof DeviceInfo, // Not a real key, just for actions column
        render: (_: any, row: DeviceInfo) => (
          <Button size='sm' color='neutral' onClick={() => navigate(`/device-details/${row.id}`)}>
            {t('general_button_labels.view')}
          </Button>
        ),
        width: '8%'
      }
    ];

    const devicesForSelectedDeviceCategory = devices.filter((device) => {
      return device.device_type?.category === selectedDeviceCategory;
    });

    return <DynamicTable columns={columnsWithActions} data={devicesForSelectedDeviceCategory} selectedRows={[]} />;
  })();

  return (
    <>
      <ResponsiveLayout>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            mt: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <TableHeader title={t('devices.title')} />
            <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', gap: 1 }}>
              <Button color='neutral' onClick={() => navigate('/register-device')} variant='solid'>
                {t('devices.add_device')}
              </Button>
              <Select
                sx={{ width: '130px' }}
                defaultValue='list'
                onChange={(event, newValue) => setViewType(newValue! as 'card' | 'list')}
              >
                <Option value='card'>Card view</Option>
                <Option value='list'>List view</Option>
              </Select>
            </Box>
          </Box>
          <Tabs
            aria-label='Devices tabs'
            defaultValue={DeviceCategories.Drone}
            onChange={(event, newValue) => setSelectedDeviceCategory(newValue! as DeviceCategories)}
          >
            <TabList>
              <Tab value={DeviceCategories.Drone}>{t('devices.device_types.drones')}</Tab>
              <Tab value={DeviceCategories.GroundStation}>{t('devices.device_types.drone_fi')}</Tab>
              <Tab value={DeviceCategories.Controller}>{t('devices.device_types.ground_controllers')}</Tab>
            </TabList>
            <Box sx={{ mt: 2 }}>{body}</Box>
          </Tabs>
        </Box>
      </ResponsiveLayout>
    </>
  );
});

export default Devices;
